/* ============================================================
   Plush + Oak — Guided Configurator
   Self-contained. No theme internals depended on except the
   `cart:refresh` event Prestige already listens for.
   ============================================================ */

(function () {
  'use strict';

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var dec = parts[1];

    if (!format) return '$' + whole + '.' + dec;

    return format
      .replace(/\{\{\s*amount\s*\}\}/g, whole + '.' + dec)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  function addWeeks(from, weeks) {
    var d = new Date(from.getTime());
    d.setDate(d.getDate() + weeks * 7);
    return d;
  }

  function formatDate(d, withYear) {
    var opts = { month: 'short', day: 'numeric' };
    if (withYear) opts.year = 'numeric';
    return d.toLocaleDateString(undefined, opts);
  }

  function announce(msg) {
    var el = document.getElementById('status-announcement');
    if (el) el.textContent = msg;
  }

  class PoConfigurator extends HTMLElement {
    connectedCallback() {
      this.dataStore = JSON.parse(this.querySelector('[data-poc-data]').textContent);
      this.variants = this.dataStore.variants;
      this.moneyFormat = this.dataStore.moneyFormat;
      this.optionCount = this.dataStore.optionCount;

      this.current = null;
      this.addonTotal = 0;
      this.qty = 1;

      this.els = {
        price: this.querySelector('[data-poc-price]'),
        compare: this.querySelector('[data-poc-compare]'),
        total: this.querySelector('[data-poc-total]'),
        base: this.querySelector('[data-poc-base]'),
        atc: this.querySelector('[data-poc-atc]'),
        atcLabel: this.querySelector('[data-poc-atc-label]'),
        atcCount: this.querySelector('[data-poc-atc-count]'),
        error: this.querySelector('[data-poc-error]'),
        stage: this.querySelector('[data-poc-stage]'),
        thumbs: this.querySelector('[data-poc-thumbs]'),
        sticky: this.querySelector('[data-poc-sticky]'),
        stickyPrice: this.querySelector('[data-poc-sticky-price]'),
        stickyName: this.querySelector('[data-poc-sticky-name]'),
        stickyThumb: this.querySelector('[data-poc-sticky-thumb]'),
        counter: this.querySelector('[data-poc-counter]'),
        bar: this.querySelector('[data-poc-bar]'),
        barFill: this.querySelector('[data-poc-bar-fill]'),
        addonRows: this.querySelector('[data-poc-addon-rows]'),
        variantId: this.querySelector('[data-poc-variant-id]'),
        stickyAtc: this.querySelector('[data-poc-sticky-atc]'),
        shipNote: this.querySelector('[data-poc-shipnote]'),
        shipWindow: this.querySelector('[data-poc-shipwindow]'),
        shipTransit: this.querySelector('[data-poc-shiptransit]'),
        bundleOffer: this.querySelector('[data-poc-bundle-offer]'),
        bundleText: this.querySelector('[data-poc-bundle-text]'),
        freeShipOffer: this.querySelector('[data-poc-freeship-offer]'),
        freeShipText: this.querySelector('[data-poc-freeship-text]'),
        estZip: this.querySelector('[data-poc-est-zip]'),
        estCountry: this.querySelector('[data-poc-est-country]'),
        estProvince: this.querySelector('[data-poc-est-province]'),
        estGo: this.querySelector('[data-poc-est-go]'),
        estResult: this.querySelector('[data-poc-est-result]'),
        qtyInput: this.querySelector('[data-poc-qty]'),
        qtyValue: this.querySelector('[data-poc-qty-value]'),
        qtyUp: this.querySelector('[data-poc-qty-up]'),
        qtyDown: this.querySelector('[data-poc-qty-down]'),
        deposit: this.querySelector('[data-poc-deposit]')
      };

      this.form = this.querySelector('form[action*="/cart/add"]') || this.querySelector('form');

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));

      if (this.form) this.form.addEventListener('submit', this.onAdd.bind(this));

      /* SPD Split Payment binds the same add-to-cart and submits the form itself when a
         deposit is selected, which races this handler: whichever wins, the cart ends up
         either missing the accessories or missing the deposit flag. Claim the click in
         the capture phase so exactly one handler builds the cart — this one, which knows
         about both the accessories and the deposit. */
      var selfAtc = this;

      if (this.els.atc) {
        this.els.atc.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
          selfAtc.onAdd(e);
        }, true);
      }

      var stickyReview = this.querySelector('[data-poc-sticky-review]');
      if (stickyReview) {
        var summaryEl = this.querySelector('.poc-summary');
        stickyReview.addEventListener('click', function () {
          if (!summaryEl) return;
          summaryEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }

      if (this.els.stickyAtc && this.form) {
        var form = this.form;
        this.els.stickyAtc.addEventListener('click', function () {
          if (form.requestSubmit) { form.requestSubmit(); } else { form.dispatchEvent(new Event('submit', { cancelable: true })); }
        });
      }

      this.initPay();
      this.initGallery();
      this.initQty();
      this.initDrawer();
      this.initEstimator();
      this.resolveVariant();
      this.syncAddons();
      this.observeSticky();

      /* Only now is the multi-item handler bound. Until this flag is set, the inline guard
         blocks a native form submit — which would add the bed without its accessories. */
      this.dataset.pocReady = '1';
      if (this.els.atc) this.els.atc.disabled = false;
      if (this.els.stickyAtc) this.els.stickyAtc.disabled = false;
    }

    /* ---------- matching the add-ons to the bed ----------

       Customers overwhelmingly want the set to match, so an add-on follows the
       bed's colour and finish instead of sitting on whatever variant it happened
       to render with.

       Finishes cannot be matched by string. This bed says "Leg Color / Black",
       the stool says "Base Hardware / Matte Black", the Charlie says "Hardware
       Colour / Brushed Brass" where the Fleur says "Bronzed Brass". Matching is
       done on the family and each product keeps its own word for it. */
    metalFamily(value) {
      var v = String(value || '').trim().toLowerCase();
      var fams = {
        black: ['black', 'matte black', 'powder black', 'powder coated black'],
        brass: ['brass', 'bronzed brass', 'brushed brass', 'antique brass'],
        chrome: ['chrome', 'polished chrome']
      };
      for (var k in fams) { if (fams[k].indexOf(v) !== -1) return k; }
      return null;
    }

    /* An option is a finish when its values are finishes — not because of what it
       is called. "Leg Height" contains the word leg and is not one; "Base
       Material" contains neither colour nor hardware and is. */
    optionKind(name, values) {
      var metal = 0;
      for (var i = 0; i < values.length; i++) { if (this.metalFamily(values[i])) metal++; }
      if (metal && metal === values.length) return 'metal';

      var n = String(name || '').toLowerCase();
      if (n.indexOf('color') !== -1 || n.indexOf('colour') !== -1) return 'colour';
      return null;
    }

    /* What the bed is currently set to, by dimension. */
    bedChoice() {
      var out = { colour: null, metal: null };

      for (var i = 1; i <= this.optionCount; i++) {
        var checked = this.querySelector('input[name="poc-option' + i + '"]:checked');
        if (!checked) continue;

        var values = Array.prototype.map.call(
          this.querySelectorAll('input[name="poc-option' + i + '"]'),
          function (el) { return el.value; }
        );
        var kind = this.optionKind(checked.dataset.pocOptname || '', values);
        if (kind && !out[kind]) out[kind] = checked.value;
      }

      return out;
    }

    addonData(row) {
      if (row._pocData !== undefined) return row._pocData;

      var tag = row.querySelector('[data-poc-addon-variants]');
      try {
        row._pocData = tag ? JSON.parse(tag.textContent) : null;
      } catch (e) {
        row._pocData = null;
      }
      return row._pocData;
    }

    matchAddons() {
      var want = this.bedChoice();
      if (!want.colour && !want.metal) return;

      var self = this;

      Array.prototype.forEach.call(this.querySelectorAll('.poc-addon'), function (row) {
        /* A shopper who set this row's option themselves keeps it. */
        if (row.dataset.pocAddonPinned === '1') return;

        var data = self.addonData(row);
        if (!data || !data.variants || !data.variants.length) return;

        var cb = row.querySelector('input[data-poc-addon]');
        if (!cb) return;

        var currentId = parseInt(cb.dataset.pocAddon, 10);
        var current = data.variants.filter(function (v) { return v.id === currentId; })[0]
          || data.variants[0];

        /* Start from what the row already is and override only the dimensions the
           bed actually determines, so a chosen model or height is preserved. */
        var target = current.options.slice();

        (data.options || []).forEach(function (name, idx) {
          var values = data.variants.map(function (v) { return v.options[idx]; })
            .filter(function (v, i, a) { return a.indexOf(v) === i; });
          var kind = self.optionKind(name, values);
          if (!kind || !want[kind]) return;

          if (values.indexOf(want[kind]) !== -1) {
            target[idx] = want[kind];
            return;
          }

          if (kind === 'metal') {
            var fam = self.metalFamily(want.metal);
            for (var i = 0; i < values.length; i++) {
              if (self.metalFamily(values[i]) === fam) { target[idx] = values[i]; return; }
            }
          }
          /* No equivalent — the Oxford stocks nine colourways, not fourteen. The
             row keeps what it had rather than falling back to something wrong. */
        });

        var next = data.variants.filter(function (v) {
          return v.available && v.options.every(function (o, i) { return o === target[i]; });
        })[0];

        if (!next || next.id === currentId) return;

        cb.dataset.pocAddon = next.id;
        cb.dataset.pocPrice = next.price;

        var priceEl = row.querySelector('.poc-addon__price');
        if (priceEl && next.priceFormatted) {
          var per = priceEl.querySelector('.poc-addon__per');
          priceEl.textContent = next.priceFormatted;
          if (per) priceEl.appendChild(per);
        }

        var img = row.querySelector('.poc-addon__img');
        if (img && img.tagName === 'IMG' && next.image) img.src = next.image;

        var picker = row.querySelector('[data-poc-addon-variant]');
        if (picker) {
          for (var o = 0; o < picker.options.length; o++) {
            if (parseInt(picker.options[o].value, 10) === next.id) { picker.selectedIndex = o; break; }
          }
        }
      });

      this.syncAddons();
    }

    /* ---------- selection ---------- */

    selectedOptions() {
      var out = [];
      for (var i = 1; i <= this.optionCount; i++) {
        var input = this.querySelector('input[name="poc-option' + i + '"]:checked');
        out.push(input ? input.value : null);
      }
      return out;
    }

    resolveVariant() {
      var sel = this.selectedOptions();
      var match = this.variants.find(function (v) {
        return sel.every(function (val, i) { return val === null || v.options[i] === val; });
      });

      this.current = match || null;
      this.updateAvailability(sel);
      this.matchAddons();
      this.render();
    }

    /* Grey out option values that cannot combine with the rest of the selection. */
    updateAvailability(sel) {
      var self = this;

      for (var pos = 1; pos <= this.optionCount; pos++) {
        var inputs = this.querySelectorAll('input[name="poc-option' + pos + '"]');

        Array.prototype.forEach.call(inputs, function (input) {
          var trial = sel.slice();
          trial[input.dataset.pocPos - 1] = input.value;

          var possible = self.variants.some(function (v) {
            return trial.every(function (val, i) {
              return val === null || v.options[i] === val;
            }) && v.available;
          });

          var wrap = input.closest('[data-poc-opt]');
          if (wrap) wrap.classList.toggle('is-unavailable', !possible);
          input.disabled = !possible && !input.checked;
        });
      }
    }

    /* ---------- rendering ---------- */

    render() {
      var v = this.current;
      var base = v ? v.price : this.dataStore.minPrice;
      /* Single source of truth — orderTotal() accounts for bed quantity and each
         add-on's own quantity. */
      var total = this.orderTotal();

      if (this.els.price) {
        this.els.price.textContent = formatMoney(base, this.moneyFormat);
        this.flash(this.els.price);
      }

      if (this.els.compare) {
        var showCompare = v && v.compare_at_price && v.compare_at_price > v.price;
        this.els.compare.textContent = showCompare ? formatMoney(v.compare_at_price, this.moneyFormat) : '';
        this.els.compare.hidden = !showCompare;
      }

      if (this.els.base) {
        this.els.base.textContent = formatMoney(base * this.qty, this.moneyFormat);
        var baseLabel = this.els.base.parentElement && this.els.base.parentElement.querySelector('dt');
        if (baseLabel) {
          baseLabel.textContent = this.qty > 1
            ? this.dataStore.productTitle + ' × ' + this.qty
            : this.dataStore.productTitle;
        }
      }

      if (this.els.total) {
        this.els.total.textContent = formatMoney(total, this.moneyFormat);
        this.flash(this.els.total);
      }

      if (this.els.stickyPrice) this.els.stickyPrice.textContent = formatMoney(total, this.moneyFormat);

      if (this.els.stickyName && v) this.els.stickyName.textContent = v.title;

      /* Everything else in the build, named — the bar previously showed only the bed
         and its colour, so a total that had grown by three add-ons was unexplained. */
      var stickyAddons = this.querySelector('[data-poc-sticky-addons]');
      if (stickyAddons) {
        var picked = [];
        var selfS = this;
        this.querySelectorAll('input[data-poc-addon]:checked').forEach(function (input) {
          var label = input.dataset.pocLabel || '';
          var q = selfS.addonQty(input);
          picked.push(q > 1 ? (label + ' \u00d7 ' + q) : label);
        });
        stickyAddons.textContent = picked.length ? ('plus ' + picked.join(', ')) : '';
        stickyAddons.title = picked.join(', ');
      }

      if (this.els.variantId && v) this.els.variantId.value = v.id;

      /* per-step chosen value labels */
      var self = this;
      this.querySelectorAll('[data-poc-step-value]').forEach(function (node) {
        var pos = node.dataset.pocStepValue;
        var checked = self.querySelector('input[name="poc-option' + pos + '"]:checked');
        node.textContent = checked ? checked.dataset.pocLabel : '';
        var step = node.closest('.poc-step');
        if (step) step.classList.toggle('is-complete', !!checked);
      });

      this.renderSpecs();
      this.renderShipWindow();
      this.renderOffers();
      this.renderDeposit();
      this.updateButton();
      this.updateGallery();
      this.updateUrl();
    }

    makeRow(label, value, cls) {
      var row = document.createElement('div');
      row.className = 'poc-summary__row' + (cls ? ' ' + cls : '');
      var dt = document.createElement('dt');
      dt.textContent = label;
      var dd = document.createElement('dd');
      dd.textContent = value;
      row.appendChild(dt);
      row.appendChild(dd);
      return row;
    }

    /* "Your build": the chosen colour, hardware and quoted lead time. */
    renderSpecs() {
      var host = this.querySelector('[data-poc-spec-rows]');
      if (!host) return;

      var self = this;
      host.textContent = '';

      this.querySelectorAll('[data-poc-step-value]').forEach(function (node) {
        var pos = node.dataset.pocStepValue;
        var checked = self.querySelector('input[name="poc-option' + pos + '"]:checked');
        if (!checked) return;

        var step = node.closest('.poc-step');
        var nameEl = step ? step.querySelector('.poc-step__name') : null;
        var label = nameEl ? nameEl.textContent.trim() : 'Option ' + pos;
        host.appendChild(self.makeRow(label, checked.dataset.pocLabel));
      });

      var lead = this.isRush() ? this.dataStore.strings.leadRush : this.dataStore.strings.leadStandard;
      if (lead) {
        host.appendChild(this.makeRow(this.dataStore.strings.leadLabel || 'Ships in', lead));
      }

      var win = this.shipWindowText();
      if (win) {
        host.appendChild(this.makeRow(this.dataStore.strings.shipWindowLabel || 'Ships between', win));
      }

      /* The same dates, repeated beside the rush control that changes them. Ticking
         the upgrade there otherwise updates a figure 300px further down the page,
         which is the one place a buyer needs to see the effect immediately. */
      var live = this.querySelector('[data-poc-rush-ship]');
      if (live) {
        var parts = [];
        if (lead) { parts.push((this.dataStore.strings.leadLabel || 'Ships in') + ' ' + lead); }
        if (win) { parts.push((this.dataStore.strings.shipWindowLabel || 'Ships between') + ' ' + win); }
        live.textContent = parts.join('  \u00b7  ');
      }

      var stickyShip = this.querySelector('[data-poc-sticky-ship]');
      if (stickyShip) {
        stickyShip.textContent = win ? ((this.dataStore.strings.shipWindowLabel || 'Ships between') + ' ' + win) : '';
      }
    }

    initQty() {
      if (!this.els.qtyInput) return;
      var self = this;

      var bump = function (delta) {
        self.setQty(self.qty + delta);
      };

      if (this.els.qtyUp) this.els.qtyUp.addEventListener('click', function () { bump(1); });
      if (this.els.qtyDown) this.els.qtyDown.addEventListener('click', function () { bump(-1); });

      this.els.qtyInput.addEventListener('change', function () {
        self.setQty(parseInt(self.els.qtyInput.value, 10));
      });
    }

    setQty(next) {
      if (isNaN(next)) next = 1;
      next = Math.max(1, Math.min(99, next));

      this.qty = next;
      if (this.els.qtyInput) this.els.qtyInput.value = next;
      if (this.els.qtyValue) this.els.qtyValue.textContent = next;
      if (this.els.qtyDown) this.els.qtyDown.disabled = next <= 1;

      /* Add-ons flagged as per-bed (the Rush upgrade) are priced against the bed
         count, so they follow it without exception. Letting a shopper pin their
         own number here is what put rush quantity and bed quantity out of step
         in the cart, which the variant title then had to apologise for. */
      var self = this;
      this.querySelectorAll('[data-poc-addon-qty][data-poc-follows-bed]').forEach(function (el) {
        el.value = self.qty;
      });

      this.syncAddons();
    }

    addonQtyInput(checkbox) {
      var wrap = checkbox.closest('.poc-addon');
      return wrap ? wrap.querySelector('[data-poc-addon-qty]') : null;
    }

    /* Each accessory carries its own quantity — 6 beds does not imply 6 pillows. */
    addonQty(checkbox) {
      var el = this.addonQtyInput(checkbox);
      var n = el ? parseInt(el.value, 10) : 1;
      return (isNaN(n) || n < 1) ? 1 : Math.min(99, n);
    }

    isRush() {
      return !!this.querySelector('input[data-poc-addon][data-poc-rush]:checked');
    }

    /* Total units in the order — this is what the bundle discount counts,
       so 3 beds on their own qualify just as 1 bed + 2 extras does. */
    itemCount() {
      var self = this;
      var n = this.qty;
      this.querySelectorAll('input[data-poc-addon]:checked').forEach(function (input) {
        n += self.addonQty(input);
      });
      return n;
    }

    /* Units that count toward the bundle. Deliberately NOT itemCount(), which
       feeds the Add to cart badge and must keep counting everything going into
       the cart. The Rush Order Upgrade is a production fee, not a piece of
       furniture: it must not COUNT toward the 3-item threshold and it must not
       be DISCOUNTED when the threshold is met. Because po-rush-sync.js keeps
       the rush quantity equal to the bed count, counting it made every rushed
       2-bed order read as 4 items and unlock 10% it had not earned.

       The Shopify discount enforces the real rule — its entitlement is the
       merchandising collections and Rush is in none of them — so this exists to
       keep the builder's message agreeing with what the cart will actually do.
       Courtney, 2026-09-19. */
    bundleCount() {
      var self = this;
      var n = this.qty;
      this.querySelectorAll('input[data-poc-addon]:checked').forEach(function (input) {
        if (input.hasAttribute('data-poc-rush')) return;
        n += self.addonQty(input);
      });
      return n;
    }

    /* What the 10% is actually taken off — Rush is paid in full. */
    bundleBase() {
      var base = this.orderTotal() - (this.rushTotal || 0);
      return base > 0 ? base : 0;
    }

    orderTotal() {
      var base = this.current ? this.current.price : this.dataStore.minPrice;
      return (base * this.qty) + this.addonTotal;
    }

    /* Turn the production window into real calendar dates, so "6–8 weeks" becomes
       something a buyer can hold against their own diary. */
    shipWindowText() {
      var w = this.dataStore.leadWeeks;
      if (!w) return '';

      var rush = this.isRush();
      var minW = rush ? w.rushMin : w.standardMin;
      var maxW = rush ? w.rushMax : w.standardMax;

      var now = new Date();
      var from = addWeeks(now, minW);
      var to = addWeeks(now, maxW);
      var crossesYear = to.getFullYear() !== now.getFullYear();

      return formatDate(from, crossesYear) + ' – ' + formatDate(to, crossesYear);
    }

    renderShipWindow() {
      var text = this.shipWindowText();
      if (!text) return;

      var label = this.dataStore.strings.shipWindowLabel || 'Ships between';
      var full = label + ' ' + text;

      if (this.els.shipWindow && this.els.shipWindow.textContent !== full) {
        this.els.shipWindow.textContent = full;
        if (this.els.shipNote) this.flash(this.els.shipNote);
      }

      if (this.els.shipTransit) {
        var note = this.dataStore.strings.transitNote || '';
        var rushWord = this.isRush()
          ? this.dataStore.strings.leadRush
          : this.dataStore.strings.leadStandard;
        this.els.shipTransit.textContent = note ? rushWord + ' production, ' + note : rushWord;
      }
    }

    /* Bundle discount + free-shipping progress, both driven by live cart-side rules. */
    renderOffers() {
      var o = this.dataStore.offers;
      if (!o) return;

      var strings = this.dataStore.strings;
      var count = this.bundleCount();
      var total = this.orderTotal();

      if (this.els.bundleText) {
        var remaining = o.bundleQty - count;
        if (remaining > 0) {
          var msg = (strings.bundleMsg || '').replace('[n]', remaining);
          this.els.bundleText.textContent = msg;
          if (this.els.bundleOffer) this.els.bundleOffer.classList.remove('is-unlocked');
        } else {
          var saving = Math.round(this.bundleBase() * (o.bundlePercent / 100));
          var applied = (strings.bundleAppliedMsg || '')
            .replace('[amount]', formatMoney(saving, this.moneyFormat));
          this.els.bundleText.textContent = applied;
          if (this.els.bundleOffer) this.els.bundleOffer.classList.add('is-unlocked');
        }
      }

      if (this.els.freeShipText && o.freeShipThreshold > 0) {
        var short = o.freeShipThreshold - total;
        if (short > 0) {
          this.els.freeShipText.textContent = (strings.freeShipMsg || '')
            .replace('[amount]', formatMoney(short, this.moneyFormat));
          if (this.els.freeShipOffer) this.els.freeShipOffer.classList.remove('is-unlocked');
        } else {
          this.els.freeShipText.textContent = strings.freeShipAppliedMsg || '';
          if (this.els.freeShipOffer) this.els.freeShipOffer.classList.add('is-unlocked');
        }
      }
    }

    /* What they'd pay today under the production deposit programme. */
    /* SPD Split Payment injects its own "How would you like to pay?" radios into this
       form. It owns the choice and the charging; we only read it, so the deposit line
       can show the actual figure — which SPD's widget does not display.

       Its widget mounts asynchronously, so the listener is delegated rather than bound
       to elements that may not exist yet. */
    initPay() {
      var self = this;
      this.payMode = 'full';

      var onChange = function (e) {
        var t = e.target;
        if (!t || t.type !== 'radio') return;
        if (!t.closest('[class*="spp2__widget"]')) return;
        self.payMode = /full/i.test(t.value) ? 'full' : 'deposit';
        self.render();
      };
      this.addEventListener('change', onChange);
      document.addEventListener('change', onChange);

      // Catch a selection SPD may have restored before we attached.
      this._payPoll = setInterval(function () {
        var w = document.querySelector('[class*="spp2__widget"]');
        if (!w) return;
        clearInterval(self._payPoll);
        var checked = w.querySelector('input[type="radio"]:checked');
        if (checked) {
          self.payMode = /full/i.test(checked.value) ? 'full' : 'deposit';
          self.render();
        }
      }, 400);
      setTimeout(function () { clearInterval(self._payPoll); }, 12000);
    }

    /* What the customer is actually charged today. SPD applies the split to the whole
       order, so this is a straight halving of the order total. */
    /* SPD owns these radios; read them at submit time rather than trusting the cached
       payMode, which can lag if its widget re-rendered. Values are 'full' / '0'. */
    depositSelected() {
      var radio = document.querySelector('input[name="payment-option"]:checked');
      if (radio) return !/full/i.test(radio.value);
      return this.payMode === 'deposit';
    }

    dueToday() {
      var d = this.dataStore.deposit;
      var pct = (d && d.percent ? d.percent : 50) / 100;
      return Math.round(this.orderTotal() * pct);
    }

    addonPrice(input) {
      var v = parseInt(input.dataset.pocPrice, 10);
      return isNaN(v) ? 0 : v;
    }

    renderDeposit() {
      var d = this.dataStore.deposit;
      if (!this.els.deposit) return;

      var on = this.payMode === 'deposit' && d && d.percent && d.text;
      this.els.deposit.hidden = !on;
      if (!on) { this.els.deposit.textContent = ''; return; }

      var due = this.dueToday();
      this.els.deposit.textContent = d.text.replace('[amount]', formatMoney(due, this.moneyFormat));
    }

    flash(node) {
      node.classList.remove('poc-price-flash');
      void node.offsetWidth;
      node.classList.add('poc-price-flash');
    }

    updateButton() {
      var btn = this.els.atc;
      if (!btn) return;

      var v = this.current;
      /* Count units, not lines, so this agrees with the bundle-discount counter. */
      var count = this.itemCount();

      if (!v) {
        btn.disabled = true;
        if (this.els.stickyAtc) this.els.stickyAtc.disabled = true;
        this.els.atcLabel.textContent = this.dataStore.strings.unavailable;
        if (this.els.atcCount) this.els.atcCount.textContent = '';
        return;
      }

      if (!v.available) {
        btn.disabled = true;
        if (this.els.stickyAtc) this.els.stickyAtc.disabled = true;
        this.els.atcLabel.textContent = this.dataStore.strings.soldOut;
        if (this.els.atcCount) this.els.atcCount.textContent = '';
        return;
      }

      btn.disabled = false;
      if (this.els.stickyAtc) this.els.stickyAtc.disabled = false;
      this.els.atcLabel.textContent = this.dataStore.strings.addToCart;
      if (this.els.atcCount) {
        this.els.atcCount.textContent = count > 1 ? '(' + count + ' items)' : '';
      }
    }

    /* ---------- gallery ---------- */

    initGallery() {
      this.mediaEls = Array.prototype.slice.call(this.querySelectorAll('[data-poc-media]'));
      this.thumbEls = Array.prototype.slice.call(this.querySelectorAll('[data-poc-thumb]'));
      this.els.counter = this.querySelector('[data-poc-counter]');
      this.visible = this.mediaEls.slice();

      var self = this;
      var prev = this.querySelector('[data-poc-prev]');
      var next = this.querySelector('[data-poc-next]');
      if (prev) prev.addEventListener('click', function () { self.step(-1); });
      if (next) next.addEventListener('click', function () { self.step(1); });

      // On a phone the stage runs edge to edge, and an edge-to-edge photo that
      // ignores a thumb feels broken. Horizontal intent only: anything steeper
      // than 45 degrees is the page being scrolled, so leave it alone.
      // Attached unconditionally rather than behind a `pointer: coarse` check.
      // That query is evaluated once at load, so a tablet rotated afterwards — or
      // any device that resolves it late — would silently lose swipe for the rest
      // of the session. Touch events simply never fire on a mouse-only device.
      var stage = this.querySelector('[data-poc-stage]');
      if (stage) {
        var x0 = 0, y0 = 0, tracking = false;

        stage.addEventListener('touchstart', function (e) {
          if (e.touches.length !== 1) { tracking = false; return; }
          x0 = e.touches[0].clientX;
          y0 = e.touches[0].clientY;
          tracking = true;
        }, { passive: true });

        stage.addEventListener('touchend', function (e) {
          if (!tracking) return;
          tracking = false;
          var t = e.changedTouches[0];
          var dx = t.clientX - x0;
          var dy = t.clientY - y0;
          if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
          self.step(dx < 0 ? 1 : -1);
        }, { passive: true });
      }
    }

    currentColourSlug() {
      var input = this.querySelector('input[name="poc-option1"]:checked');
      return input ? input.value.toLowerCase().trim() : null;
    }

    hydrate(img) {
      if (img && !img.getAttribute('src') && img.dataset.pocSrc) {
        img.setAttribute('src', img.dataset.pocSrc);
        if (img.dataset.pocSrcset) img.setAttribute('srcset', img.dataset.pocSrcset);
      }
    }

    hydrateThumb(btn) {
      var img = btn.querySelector('img');
      if (img && !img.getAttribute('src') && img.dataset.pocThumbSrc) {
        img.setAttribute('src', img.dataset.pocThumbSrc);
      }
    }

    pauseMedia(el) {
      if (el && el.dataset.pocIsVideo !== undefined && typeof el.pause === 'function') el.pause();
    }

    /* Narrow the gallery to the media tagged with the chosen colour. Falls back to
       the full set if a colour has no dedicated photography. */
    applyColourFilter() {
      var slug = this.currentColourSlug();
      var self = this;

      var set = this.mediaEls.filter(function (m) { return m.dataset.pocColour === slug; });
      if (!set.length) set = this.mediaEls.slice();

      var ids = {};
      set.forEach(function (m) { ids[m.dataset.pocMedia] = true; });

      this.mediaEls.forEach(function (m) {
        var on = !!ids[m.dataset.pocMedia];
        m.classList.toggle('is-hidden', !on);
        if (!on) { m.classList.remove('is-active'); self.pauseMedia(m); }
      });

      this.thumbEls.forEach(function (t) {
        var on = !!ids[t.dataset.pocThumb];
        t.classList.toggle('is-hidden', !on);
        if (on) self.hydrateThumb(t);
      });

      this.visible = set;
    }

    updateGallery() {
      var v = this.current;
      if (!v || !this.els.stage) return;

      var colorInput = this.querySelector('input[name="poc-option1"]:checked');
      var wash = colorInput ? colorInput.dataset.pocHex : null;
      if (wash) {
        this.els.stage.style.setProperty('--poc-wash', wash);
        this.els.stage.classList.add('is-washing');
        clearTimeout(this._washTimer);
        var stage = this.els.stage;
        this._washTimer = setTimeout(function () { stage.classList.remove('is-washing'); }, 520);
      }

      this.applyColourFilter();

      // Prefer the variant's own shot; otherwise open on the first of this colour.
      var target = null;
      if (v.featured_media_id) {
        target = this.visible.filter(function (m) {
          return m.dataset.pocMedia === String(v.featured_media_id);
        })[0];
      }
      if (!target) target = this.visible[0];
      if (target) this.showMedia(target.dataset.pocMedia);
    }

    showMedia(mediaId) {
      var self = this;

      this.mediaEls.forEach(function (m) {
        var active = m.dataset.pocMedia === mediaId && !m.classList.contains('is-hidden');
        if (active && m.tagName === 'IMG') self.hydrate(m);
        if (!active) self.pauseMedia(m);
        m.classList.toggle('is-active', active);
      });

      /* Keep the sticky bar showing the bed being configured. Reuses the shot the
         gallery has already loaded, so it costs no extra request. */
      if (this.els.stickyThumb) {
        var activeShot = this.mediaEls.filter(function (m) {
          return m.tagName === 'IMG' && m.classList.contains('is-active');
        })[0];

        if (activeShot) {
          var shotSrc = activeShot.currentSrc || activeShot.getAttribute('src');

          if (shotSrc && this.els.stickyThumb.getAttribute('src') !== shotSrc) {
            this.els.stickyThumb.setAttribute('src', shotSrc);
          }
        }
      }

      this.thumbEls.forEach(function (t) {
        var on = t.dataset.pocThumb === mediaId;
        t.setAttribute('aria-current', on ? 'true' : 'false');
      });

      // Pre-load the neighbours so arrow presses are instant.
      var i = this.indexOf(mediaId);
      [i - 1, i + 1].forEach(function (n) {
        var el = self.visible[n];
        if (el && el.tagName === 'IMG') self.hydrate(el);
      });

      this.updateCounter();
    }

    indexOf(mediaId) {
      for (var i = 0; i < this.visible.length; i++) {
        if (this.visible[i].dataset.pocMedia === mediaId) return i;
      }
      return 0;
    }

    activeId() {
      var a = this.querySelector('[data-poc-media].is-active');
      return a ? a.dataset.pocMedia : (this.visible[0] ? this.visible[0].dataset.pocMedia : null);
    }

    step(delta) {
      if (!this.visible.length) return;
      var i = this.indexOf(this.activeId());
      var n = (i + delta + this.visible.length) % this.visible.length;
      this.showMedia(this.visible[n].dataset.pocMedia);
    }

    updateCounter() {
      var total = this.visible.length;
      var i = this.indexOf(this.activeId()) + 1;

      /* els.counter was never bound, so this had always no-opped and the counter
         rendered empty. It is bound now — it is the accessible half of the position
         bar below, which is aria-hidden. */
      if (this.els.counter) {
        this.els.counter.textContent = total > 1 ? i + ' / ' + total : '';
      }

      if (this.els.bar && this.els.barFill) {
        /* One segment per image, slid to the active index. Hidden outright when there
           is only one shot, since a full-width bar would imply more to swipe to. */
        this.els.bar.hidden = total < 2;

        if (total > 1) {
          this.els.barFill.style.width = (100 / total) + '%';
          this.els.barFill.style.transform = 'translateX(' + ((i - 1) * 100) + '%)';
        }
      }
    }

    updateUrl() {
      if (!this.current || !window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', this.current.id);
      window.history.replaceState({}, '', url.toString());
    }

    /* ---------- add-ons ---------- */

    syncAddons() {
      var self = this;
      var rows = [];
      this.addonTotal = 0;
      this.rushTotal = 0;

      this.querySelectorAll('input[data-poc-addon]:checked').forEach(function (input) {
        var unit = parseInt(input.dataset.pocPrice, 10) || 0;
        var n = self.addonQty(input);
        self.addonTotal += unit * n;
        if (input.hasAttribute('data-poc-rush')) self.rushTotal += unit * n;
        rows.push({ name: input.dataset.pocLabel, price: unit * n, qty: n });
      });

      var addonStep = this.querySelector('[data-poc-addons-step]');
      if (addonStep) addonStep.classList.toggle('is-complete', rows.length > 0);

      if (this.els.addonRows) {
        this.els.addonRows.textContent = '';
        rows.forEach(function (r) {
          var label = r.qty > 1 ? r.name + ' × ' + r.qty : r.name;
          self.els.addonRows.appendChild(
            self.makeRow(label, formatMoney(r.price, self.moneyFormat), 'poc-summary__row--item')
          );
        });
      }

      var group = this.querySelector('[data-poc-addon-group]');
      if (group) group.hidden = rows.length === 0;

      this.render();
    }

    /* ---------- events ---------- */

    onChange(e) {
      var t = e.target;

      /* Add-on option picker (the stool's base hardware). Rewrites the row's variant
         id and price, then recomputes. Checked before the input test below, because
         this control is a <select> and would otherwise never be seen. */
      if (t.dataset && t.dataset.pocAddonVariant !== undefined) {
        var vrow = t.closest('.poc-addon');
        /* From here this row is the shopper's, not the bed's. */
        if (vrow) vrow.dataset.pocAddonPinned = '1';
        var vcb = vrow ? vrow.querySelector('input[data-poc-addon]') : null;
        var vopt = t.options[t.selectedIndex];
        if (vcb && vopt) {
          vcb.dataset.pocAddon = vopt.value;
          if (vopt.dataset.price) { vcb.dataset.pocPrice = vopt.dataset.price; }

          var vprice = vrow.querySelector('.poc-addon__price');
          if (vprice && vopt.dataset.priceFormatted) {
            /* Keep the "per bed" qualifier if this row carries one. */
            var vper = vprice.querySelector('.poc-addon__per');
            vprice.textContent = vopt.dataset.priceFormatted;
            if (vper) { vprice.appendChild(vper); }
          }
          this.syncAddons();
        }
        return;
      }

      if (!t.matches('input')) return;

      if (t.dataset.pocAddon !== undefined) {
        this.syncAddons();
        return;
      }

      if (t.dataset.pocAddonQty !== undefined) {
        if (t.hasAttribute('data-poc-follows-bed')) {
          t.value = this.qty;
          this.syncAddons();
          return;
        }
        var n = Math.max(1, Math.min(99, parseInt(t.value, 10) || 1));
        t.value = n;
        t.dataset.pocTouched = '1';
        this.syncAddons();
        return;
      }

      if (/^poc-option\d+$/.test(t.name)) {
        this.resolveVariant();
        announce(t.dataset.pocLabel + ' selected');
      }
    }

    onClick(e) {
      var thumb = e.target.closest('[data-poc-thumb]');
      if (thumb) {
        e.preventDefault();
        this.showMedia(thumb.dataset.pocThumb);
        return;
      }

      var step = e.target.closest('[data-poc-addon-qty-up], [data-poc-addon-qty-down]');
      if (step) {
        e.preventDefault();
        var wrap = step.closest('.poc-addon');
        var field = wrap && wrap.querySelector('[data-poc-addon-qty]');
        if (!field) return;

        var delta = step.hasAttribute('data-poc-addon-qty-up') ? 1 : -1;
        var next = Math.max(1, Math.min(99, (parseInt(field.value, 10) || 1) + delta));
        field.value = next;
        field.dataset.pocTouched = '1';
        this.syncAddons();
      }
    }

    async onAdd(e) {
      e.preventDefault();
      if (!this.current || !this.current.available) return;

      var btn = this.els.atc;
      var self = this;

      this.els.error.textContent = '';
      btn.classList.add('is-loading');

      // No selling_plan here: SPD applies the split at checkout, not per cart line.
      var items = [{ id: this.current.id, quantity: this.qty }];

      this.querySelectorAll('input[data-poc-addon]:checked').forEach(function (input) {
        items.push({ id: parseInt(input.dataset.pocAddon, 10), quantity: self.addonQty(input) });
      });

      /* Tag every line, not just the bed. A deposit that covered the bed while the
         accessories were charged in full would be the confusing half-and-half bill. */
      if (this.depositSelected()) {
        items.forEach(function (item) {
          item.properties = { '_spp2-deposit': '1' };
        });
      }

      try {
        var res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });

        var body = await res.json();

        if (!res.ok) {
          throw new Error(body.description || body.message || self.dataStore.strings.addError);
        }

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'po-configurator' } })
        );

        announce(self.dataStore.strings.added);

        // Open the slide-out cart so the add is visibly confirmed. Prestige's
        // <cart-drawer> extends DialogElement, so it exposes show(). If the cart
        // type is set to page there is no drawer and the announcement stands alone.
        var drawer = document.querySelector('cart-drawer');

        if (drawer && typeof drawer.show === 'function') {
          requestAnimationFrame(function () {
            try {
              drawer.show();
            } catch (e) {}
          });
        }
      } catch (err) {
        this.els.error.textContent = err.message || this.dataStore.strings.addError;
      } finally {
        btn.classList.remove('is-loading');
      }
    }

    /* ---------- delivery estimate ---------- */

    initEstimator() {
      if (!this.els.estGo) return;

      var self = this;
      this.els.estGo.addEventListener('click', function () { self.estimate(); });

      if (this.els.estZip) {
        this.els.estZip.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); self.estimate(); }
        });
      }

      if (this.els.estCountry) {
        /* The market is geo-located, so the country is fixed: take it from Liquid's
           localization object and fall back to Shopify.country only if that is absent. */
        var want = this.els.estCountry.dataset.pocMarket;
        if (!want) {
          want = (window.Shopify && Shopify.country === 'CA') ? 'Canada' : 'United States';
        }

        var match = Array.prototype.filter.call(this.els.estCountry.options, function (o) {
          return o.value === want;
        })[0];
        if (match) this.els.estCountry.value = want;

        this.fillProvinces();
      }
    }

    /* Shopify won't quote freight without a province/state, so mirror its own cart
       calculator: read the provinces off the selected country option. */
    fillProvinces() {
      var sel = this.els.estProvince;
      var country = this.els.estCountry;
      if (!sel || !country) return;

      var opt = country.options[country.selectedIndex];
      var list = [];
      try { list = JSON.parse(opt.getAttribute('data-provinces') || '[]'); } catch (e) { list = []; }

      sel.textContent = '';

      if (!list.length) {
        sel.hidden = true;
        return;
      }

      list.forEach(function (pair) {
        var o = document.createElement('option');
        o.value = pair[0];
        o.textContent = pair[1];
        sel.appendChild(o);
      });
      sel.hidden = false;
    }

    /* Shopify only quotes freight against a real cart, so the configured bed is added,
       quoted, and removed again. Any line we add is always taken back out in `finally`,
       and an item the shopper already had is left untouched. */
    async estimate() {
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      var strings = this.dataStore.strings;
      var out = this.els.estResult;
      var zip = (this.els.estZip.value || '').trim();
      var country = this.els.estCountry ? this.els.estCountry.value : 'Canada';
      var province = (this.els.estProvince && !this.els.estProvince.hidden) ? this.els.estProvince.value : '';

      if (!zip) { this.els.estZip.focus(); return; }
      if (!this.current) return;

      out.textContent = strings.estimateBusy;
      this.els.estGo.disabled = true;

      var addedKey = null;
      var variantId = this.current.id;

      try {
        var cart = await (await fetch(root + 'cart.js')).json();
        var already = (cart.items || []).some(function (i) { return i.variant_id === variantId; });

        if (!already) {
          var addRes = await fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: this.qty }] })
          });
          var added = await addRes.json();
          if (added && added.items && added.items[0]) addedKey = added.items[0].key;
        }

        var qs = new URLSearchParams();
        qs.set('shipping_address[zip]', zip);
        qs.set('shipping_address[country]', country);
        if (province) qs.set('shipping_address[province]', province);

        var rates = await (await fetch(root + 'cart/shipping_rates.json?' + qs.toString())).json();
        var list = (rates && rates.shipping_rates) || [];

        if (!list.length) {
          out.textContent = strings.estimateNone;
        } else {
          var cheapest = list.reduce(function (a, b) {
            return parseFloat(a.price) <= parseFloat(b.price) ? a : b;
          });
          var cents = Math.round(parseFloat(cheapest.price) * 100);
          var threshold = this.dataStore.offers ? this.dataStore.offers.freeShipThreshold : 0;

          if (threshold > 0 && this.orderTotal() >= threshold) {
            out.textContent = cheapest.name + ' — ' + (strings.freeShipAppliedMsg || 'Free shipping');
          } else {
            out.textContent = cheapest.name + ' — ' + formatMoney(cents, this.moneyFormat);
          }
        }
      } catch (err) {
        out.textContent = strings.estimateError;
      } finally {
        if (addedKey) {
          try {
            await fetch(root + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: addedKey, quantity: 0 })
            });
          } catch (e) { /* leave it: the shopper can remove it in the cart */ }
        }
        this.els.estGo.disabled = false;
      }
    }

    /* ---------- material drawer ---------- */

    initDrawer() {
      this.drawer = this.querySelector('[data-poc-drawer]');
      if (!this.drawer) return;

      var self = this;

      this.querySelectorAll('[data-poc-drawer-open]').forEach(function (b) {
        b.addEventListener('click', function () { self.openDrawer(); });
      });

      this.querySelectorAll('[data-poc-drawer-close]').forEach(function (b) {
        b.addEventListener('click', function () { self.closeDrawer(); });
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.closeDrawer();
      });
    }

    openDrawer() {
      var d = this.drawer;
      if (!d) return;

      this.lastFocus = document.activeElement;
      d.hidden = false;
      document.body.classList.add('poc-drawer-open');

      /* Force a reflow rather than waiting on requestAnimationFrame: rAF is paused in
         background tabs, which would leave the panel unhidden but never slid in. */
      void d.offsetWidth;
      d.classList.add('is-open');

      var close = d.querySelector('.poc-drawer__close');
      if (close) close.focus();
    }

    closeDrawer() {
      var d = this.drawer;
      if (!d || d.hidden) return;

      d.classList.remove('is-open');
      document.body.classList.remove('poc-drawer-open');

      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();

      clearTimeout(this._drawerTimer);
      this._drawerTimer = setTimeout(function () { d.hidden = true; }, 460);
    }

    /* ---------- sticky bar ---------- */

    observeSticky() {
      if (!this.els.sticky || !this.els.atc) return;

      var sticky = this.els.sticky;
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            sticky.classList.toggle('is-visible', !entry.isIntersecting);
          });
        },
        { rootMargin: '0px 0px -120px 0px' }
      );

      io.observe(this.els.atc);
    }
  }

  if (!customElements.get('po-configurator')) {
    customElements.define('po-configurator', PoConfigurator);
  }
})();

/* build: 20260824-173325 */